import { useCallback } from "react";
import type { Location } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Where a role was opened from, carried in history state so Back can return to
 * the list rather than to the role looked at just before this one.
 *
 * A production's screen links to its other parts, so a manager reads a role,
 * steps to the next, then the next, building a chain of /roles/:id entries. A
 * plain step back walks that chain one role at a time, when what they want is
 * the search results they started from. This remembers that list.
 */
export interface RoleFrom {
  /**
   * The history index of the list the role was opened from. Popping by the
   * difference to the current index lands on that exact entry, so its scroll
   * and filters come back the way a single step back restores them, however
   * many sibling roles were stepped through on the way.
   */
  originIdx?: number;
  /**
   * The list URL with its search and filters, as a fallback for when the index
   * cannot be used: a forward and back shuffle, or a role opened in a fresh
   * tab where the list is not behind it at all.
   */
  results: string;
}

/** The list the reader is on now, to hand to a role opened from it. */
export function roleFrom(location: Pick<Location, "pathname" | "search">): RoleFrom {
  const idx = (window.history.state as { idx?: number } | null)?.idx;
  return {
    originIdx: typeof idx === "number" ? idx : undefined,
    results: `${location.pathname}${location.search}`,
  };
}

/**
 * Going back, with somewhere to go when there is no back.
 *
 * navigate(-1) on its own is a control that does nothing on the first screen
 * of a session, which is more common here than it sounds: a link from Slack,
 * a bookmarked client, a refresh, or the redirect a gated route performs. The
 * button is right there and pressing it has no effect at all, which reads as
 * broken rather than as "there is nowhere to go".
 *
 * So it goes back when there is history and to the screen above when there is
 * not. The fallback is worked out from the path rather than passed in, so a
 * new screen gets sensible behaviour without having to remember this exists.
 */
function above(pathname: string): string {
  // A role sits under the directory it was found in.
  if (/^\/roles\/[^/]+/.test(pathname)) return "/roles";
  // Editing a client's saved filters falls back to that client's own screen.
  if (/^\/clients\/[^/]+\/edit$/.test(pathname)) return pathname.replace(/\/edit$/, "");
  // Everything about one client goes back to the dashboard, because the
  // client screen is reached from the sidebar rather than from a list.
  if (/^\/clients\//.test(pathname)) return "/";
  return "/";
}

export function useGoBack(): () => void {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(() => {
    // Opened from a list: go back to that list, not to the role read just
    // before this one. Popping by the index difference returns to the list's
    // own entry, so its scroll and filters restore; where that is not possible
    // the saved URL takes us there afresh.
    const from = (location.state as { from?: RoleFrom } | null)?.from;
    if (from) {
      const here = (window.history.state as { idx?: number } | null)?.idx;
      if (typeof from.originIdx === "number" && typeof here === "number" && here > from.originIdx) {
        navigate(from.originIdx - here);
        return;
      }
      navigate(from.results);
      return;
    }

    // React Router numbers its own history entries. Index 0 is the entry the
    // tab opened on, so there is nothing of ours behind it. The key check is
    // the same question asked of a router that has not written state yet.
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    const first = idx === 0 || (idx === undefined && location.key === "default");

    if (first) navigate(above(location.pathname), { replace: true });
    else navigate(-1);
  }, [navigate, location.key, location.pathname, location.state]);
}
